'use client'

import { useQueryClient } from '@tanstack/react-query'
import { CheckIcon, Loader2 } from 'lucide-react'
import Image from 'next/image'
import { type ChangeEvent, useCallback, useState } from 'react'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import {
  useGetReferrerProfile,
  useLinkReferralCode,
} from '@/src/queries/hooks/referralcodes'
import { getReferrerProfileQuery } from '@/src/queries/keys/referralcodes'
import { isReferralDisplayError } from '@/src/utils/referral-errors'
import { Button } from '../../ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '../../ui/dialog'
import { CircleSlashIcon } from '../../ui/icons/circle-slash-icon'
import { Input } from '../../ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip'

const baseUrl =
  (process.env.APP_BASE_URL ||
    (process.env.VERCEL_URL
      ? `https://${process.env.VERCEL_URL}`
      : undefined)) ??
  ''

export const ReferralCodeDialog = () => {
  const [referralCode, setReferralCode] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const { ampli } = useAmplitudeAnalytics()
  const queryClient = useQueryClient()
  const { mutate, reset, isSuccess, isError, isPending, isIdle, error } =
    useLinkReferralCode()
  const { data: referrerProfile } = useGetReferrerProfile()

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setReferralCode(e.target.value)

      if (!isIdle) {
        reset()
      }
    },
    [isIdle, reset],
  )

  const onSubmit = useCallback(() => {
    mutate(
      {
        // We also allow user to paste the full URL, so we need to remove the base URL and the /invite part
        referral_code: referralCode.replace(baseUrl, '').split('/').pop() ?? '',
      },
      {
        onSuccess() {
          // Refetch referrer profile to get updated data
          queryClient.invalidateQueries(getReferrerProfileQuery)

          if (ampli) {
            ampli.referralCodeLinked({
              referralCode,
              manual: true,
            })
          }

          // Close dialog with a delay after success
          setTimeout(() => {
            setIsOpen(false)
          }, 1500)
        },
      },
    )
  }, [referralCode, ampli, mutate, queryClient])

  // Show button always, but disable it when user can't link referral code
  const canLinkReferralCode = referrerProfile?.can_link_referral_code
  const hasReferralCodeApplied = referrerProfile && !canLinkReferralCode

  const onOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open)

      if (!open) {
        reset()
        setReferralCode('')
      }
    },
    [reset],
  )

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <div className='transition-all duration-500 ease-in-out opacity-100 scale-100 h-auto'>
        {hasReferralCodeApplied ? (
          <TooltipProvider>
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>
                <div>
                  <DialogTrigger asChild>
                    <Button
                      variant='outline'
                      size='sm'
                      disabled={!canLinkReferralCode}
                    >
                      Enter code
                    </Button>
                  </DialogTrigger>
                </div>
              </TooltipTrigger>
              <TooltipContent
                hideArrow
                sideOffset={6}
                className='px-2.5 py-1.5'
              >
                <div className='flex items-center gap-2'>
                  <CheckIcon className='size-3.5 text-success-text' />
                  <p className='text-xs font-semibold leading-4'>
                    Referral code applied
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <DialogTrigger asChild>
            <Button variant='outline' size='sm' disabled={!canLinkReferralCode}>
              Enter code
            </Button>
          </DialogTrigger>
        )}
      </div>
      <DialogContent
        aria-describedby='enter referral code manually'
        className='sm:max-w-[448px] w-full h-auto min-h-[unset] sm:min-w-[unset]'
        showCloseButton
        showBackButton={false}
        middle={
          <DialogTitle className='text-xl'>Enter referral code</DialogTitle>
        }
      >
        <div className='px-8 pb-8'>
          <Image
            src='https://s3.polaris.app/illustrations/referral-monkeys.png'
            className='mx-auto mb-8 flex-shrink-0'
            alt='Referral monkeys'
            width={190.029}
            height={123}
            style={{
              aspectRatio: '190.03/123.00',
            }}
          />

          <p className='text-secondary text-center mb-10'>
            If you received a referral code from someone else, enter it here.
            Currently, you can only use one referral code.
          </p>

          <div className='flex flex-col gap-2.5'>
            <Input
              placeholder='Enter referral code'
              dimension='large'
              disabled={isPending}
              onChange={onInputChange}
            />

            {isError ? (
              <div className='flex gap-2 items-center'>
                <CircleSlashIcon />

                <p className='text-sm leading-5 text-primary'>
                  {isReferralDisplayError(error)
                    ? error.message
                    : 'Invalid referral code'}
                </p>
              </div>
            ) : null}
            {isSuccess ? (
              <div className='flex gap-2 items-center'>
                <CheckIcon className='size-5 text-success-text' />

                <p className='text-sm leading-5 text-primary'>Code linked</p>
              </div>
            ) : null}
          </div>

          <Button
            type='button'
            className='w-full mt-8'
            variant='primary'
            disabled={
              isPending || referralCode.length === 0 || isSuccess || isError
            }
            onClick={onSubmit}
          >
            Apply
            {isPending ? <Loader2 className='animate-spin' /> : null}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
